import { Save } from "@mui/icons-material";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";

import { GqlMedia } from "~/__generated__";

import useFileUploader from "../../hooks/useFileUploader";
import FileDropZone from "../FileDropZone";
import FileUploadItem from "../FileUploadItem.tsx";
import PhotoCropper from "../PhotoCropper";
import getCroppedImage from "../PhotoCropper/getCroppedImage.ts";

type Props = {
    title: string;
    open: boolean;
    onClose: () => void;
    onFinished: (mediaItem: GqlMedia) => void;
};

function readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function PhotoUploadDialog({ title, open, onClose, onFinished }: Props) {
    const { t } = useTranslation("media");

    const [startingUpload, setStartingUpload] = useState(false);
    const { uploadItems: files, uploadFiles, reset, error: filedUploadError } = useFileUploader();
    const uploadItem = files[0];
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [croppedPixels, setCroppedPixels] = useState<Area | undefined>(undefined);

    const uploadFile = useCallback(
        async (name: string, imageSrc: string, pixels: Area) => {
            const croppedImage = await getCroppedImage(imageSrc, pixels);
            const blob = await fetch(croppedImage).then(res => res.blob());
            const fileName = `${name.replace(/\.[^.]+$/, "")}.jpg`;
            uploadFiles([{ name: fileName, blob }]);
        },
        [uploadFiles],
    );

    useEffect(() => {
        if (!selectedFile) return;
        readFile(selectedFile).then(imageDataUrl => setImageSrc(imageDataUrl));
    }, [selectedFile]);

    useEffect(() => {
        const resetStates = () => {
            reset();
            setStartingUpload(false);
            setSelectedFile(undefined);
            setImageSrc(undefined);
            setCroppedPixels(undefined);
        };

        if (filedUploadError) {
            resetStates();
        }

        if (uploadItem && uploadItem.type === "FinishedMedia") {
            onFinished(uploadItem.mediaItem);
            resetStates();
        }
    }, [onFinished, reset, uploadItem, filedUploadError]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{title}</DialogTitle>
            {filedUploadError && (
                <Alert severity="error">
                    <Typography>{filedUploadError.message}</Typography>
                </Alert>
            )}
            {uploadItem && (
                <DialogContent>
                    <FileUploadItem item={uploadItem} />
                </DialogContent>
            )}
            {selectedFile && !uploadItem && (
                <>
                    <DialogContent>
                        <PhotoCropper imageDataUrl={imageSrc} onCropComplete={pixels => setCroppedPixels(pixels)} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedFile(undefined)}>{t("photoUploadDialog.btnCancel")}</Button>
                        <Button
                            onClick={() => {
                                if (!imageSrc || !croppedPixels) return;
                                setStartingUpload(true);
                                uploadFile(selectedFile.name, imageSrc, croppedPixels);
                            }}
                            variant="contained"
                            startIcon={<Save />}
                            disabled={!croppedPixels && !startingUpload}
                        >
                            {t("photoUploadDialog.btnSave")}
                        </Button>
                    </DialogActions>
                </>
            )}
            {!selectedFile && !uploadItem && (
                <>
                    <DialogContent>
                        <FileDropZone sx={{ width: 1 }} onReceivedFiles={files => setSelectedFile(files[0])} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>{t("photoUploadDialog.btnCancel")}</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
